/**
 * C03 Monster — cross-feature window event listeners.
 *
 * Wires retention-event dispatches (C01 streak / C04 badge / C05 dayone /
 * boss-defeated / lesson-complete) into monster catch rolls. Producers
 * stay decoupled — they emit their event for their own reasons (mascot
 * etc.) and the monsters listener is just another observer.
 *
 * Why a separate file (not folded into monsters.ts):
 *   - Idempotent install (call from main.ts boot OR home render — listener
 *     is global, not per-render). Avoids stacking dup listeners on home
 *     re-render.
 *   - Testable in isolation — tests can construct the handler directly
 *     with a stub progress + dispatchEvent and assert catch / no-catch.
 *
 * 🔥 學習與遊戲分離: this file is 學習端 only. The boss-defeated catch
 * source comes from a learn-side game-end event (`ef:boss-defeated`) the
 * battle scene MAY emit; if it never fires, monsters simply receives 0
 * catches from that channel — kid still collects via lesson / streak / badge.
 *
 * Mirrors `season-pass-hooks.ts` install pattern for consistency.
 */

import type { FactionId } from '../../shared/types';
import { loadProgress, saveProgress } from '../../shared/progress';
import {
  autoEvolveAll,
  catchMonster,
  pickCatchTarget,
  rollCatch,
  type CatchSource,
} from './monsters';

/** Internal flag — install() is idempotent. */
let installed = false;

/**
 * Install global window listeners for monster catch rolls. Idempotent:
 * calling twice is a no-op (returns inert cleanup).
 *
 * Caller is `main.ts` (preferred) OR `home.ts` first-render (fallback).
 * Returns an uninstall fn primarily for tests so each test case can
 * isolate its listener.
 */
export function installMonsterHooks(): () => void {
  if (installed) return () => { /* idempotent — caller will get a real cleanup on the original install call */ };
  if (typeof window === 'undefined') return () => { /* noop in non-DOM env */ };

  const onStreakMilestone = (e: Event) => {
    handleCatchEvent('streak-milestone', extractFaction(e));
  };
  const onBadgeUnlocked = (e: Event) => {
    handleCatchEvent('badge-unlocked', extractFaction(e));
  };
  const onLessonComplete = (e: Event) => {
    handleCatchEvent('lesson-complete', extractFaction(e));
  };
  const onBossDefeated = (e: Event) => {
    handleCatchEvent('boss-defeated', extractFaction(e));
  };

  window.addEventListener('ef:streak-milestone', onStreakMilestone);
  window.addEventListener('ef:badge-unlocked', onBadgeUnlocked);
  window.addEventListener('ef:lesson-complete', onLessonComplete);
  window.addEventListener('ef:boss-defeated', onBossDefeated);

  installed = true;
  return () => {
    window.removeEventListener('ef:streak-milestone', onStreakMilestone);
    window.removeEventListener('ef:badge-unlocked', onBadgeUnlocked);
    window.removeEventListener('ef:lesson-complete', onLessonComplete);
    window.removeEventListener('ef:boss-defeated', onBossDefeated);
    installed = false;
  };
}

/**
 * Best-effort faction extraction from event detail. Most events ship a
 * `faction`/`subject` field; we accept either since the catch only uses
 * it as a soft bias (pickCatchTarget falls back to global pool when
 * preferFaction has nothing).
 */
function extractFaction(e: Event): FactionId | null {
  try {
    const detail = (e as CustomEvent).detail as { faction?: string; subject?: string } | undefined;
    if (!detail) return null;
    const candidate = (detail.faction ?? '') as FactionId;
    if (
      candidate === 'celestial' ||
      candidate === 'runic' ||
      candidate === 'techno' ||
      candidate === 'elemental' ||
      candidate === 'dominion'
    ) return candidate;
    // Subject → faction shortcut (avoids cross-import on factions.ts for
    // a simple 5-way switch; mirrors factionForSubject).
    switch (detail.subject) {
      case 'math':    return 'celestial';
      case 'chinese': return 'runic';
      case 'english': return 'techno';
      case 'nature':  return 'elemental';
      case 'social':  return 'dominion';
      default:        return null;
    }
  } catch {
    return null;
  }
}

/**
 * Shared event-handler body. Loads progress, rolls catch chance, picks a
 * tier-1 target (biased by faction when available), grants it, then runs
 * auto-evolve sweep so a catch that pushes the kid over EVOLVE_COST
 * immediately fuses (with celebration via `ef:monster-evolved`).
 */
function handleCatchEvent(source: CatchSource, preferFaction: FactionId | null): void {
  const p = loadProgress();
  if (!p) return;
  if (!rollCatch(source)) return;
  const target = pickCatchTarget(preferFaction);
  if (!target) return;
  const caught = catchMonster(p, target.id);
  if (!caught) return;
  // Auto-evolve any monster that just hit the threshold so the celebration
  // happens in the same tick as the catch (single mental beat for the kid).
  autoEvolveAll(p);
  // catchMonster + evolveMonster both save; one final save here is a
  // belt-and-braces for cases where neither path mutated (rare).
  try {
    saveProgress(p);
  } catch {
    // ignore — listener already best-effort
  }
}

/**
 * Direct call (no event) helper for places that DON'T have a window event
 * to leverage (e.g. an internal lesson-runner that wants to roll a catch
 * inline). Wraps loadProgress + rollCatch + catchMonster + autoEvolveAll.
 * Returns the caught monster id or null when nothing was awarded.
 */
export function tryCatchFromEvent(source: CatchSource, preferFaction: FactionId | null = null): string | null {
  const p = loadProgress();
  if (!p) return null;
  if (!rollCatch(source)) return null;
  const target = pickCatchTarget(preferFaction);
  if (!target) return null;
  const caught = catchMonster(p, target.id);
  if (!caught) return null;
  autoEvolveAll(p);
  return target.id;
}
